import { collectPageContext } from '../page-context';
import { getPageBehaviorSnapshot } from '../page-context';
import { getCartSnapshot } from '../page-context/cart/cart-state.js';
import { watchPageNavigation } from '../page-context';
import {
  isHumanGateEnabled,
  isHumanReady,
  subscribeHumanReady,
} from '../human-gate/index.js';
import { reportWidgetEvent } from './report-widget-event.js';
import type {
  PageVisitEnterReason,
  PageVisitLeaveReason,
  PageVisitFocusedProduct,
} from './widget-event-types.js';

let installed = false;
let leftForCurrentVisit = false;
/** True after the first `page_entered` for this page-load session. */
let visitEverStarted = false;
/** Pathname for the visit we are currently attributing dwell to. */
let visitPathname =
  typeof window !== 'undefined' ? window.location.pathname : '/';
/** Fallback dwell clock when behavior watchers have not reset yet. */
let visitStartedAt = Date.now();
let unsubscribeHuman: (() => void) | null = null;

function currentPathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '/';
}

function resolvePageType(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const context = collectPageContext({
      url: window.location.href,
      skipBehavior: true,
      skipCart: true,
    });
    return context.pageType ?? undefined;
  } catch {
    return undefined;
  }
}

function mapFocusedProduct(): PageVisitFocusedProduct | undefined {
  const focus = getPageBehaviorSnapshot().focusedProduct;
  if (!focus) return undefined;
  return {
    product_name: focus.productName,
    product_url: focus.productUrl,
    visibility_ratio: focus.visibilityRatio,
  };
}

function mapCartFields() {
  const cart = getCartSnapshot();
  if (!cart) return {};
  return {
    ...(cart.itemsCount != null ? { cart_items_count: cart.itemsCount } : {}),
    ...(cart.cartValue != null ? { cart_value: cart.cartValue } : {}),
    ...(cart.currency ? { cart_currency: cart.currency } : {}),
    cart_confidence: cart.confidence,
  };
}

function buildLeavePayload(reason: PageVisitLeaveReason) {
  const behavior = getPageBehaviorSnapshot();
  const fallbackMs = Math.max(0, Date.now() - visitStartedAt);
  const timeOnPageMs =
    behavior.timeOnPageMs > 0 ? behavior.timeOnPageMs : fallbackMs;

  return {
    pathname: visitPathname,
    page_type: resolvePageType(),
    reason,
    time_on_page_ms: timeOnPageMs,
    max_scroll_depth_percent: behavior.maxScrollDepthPercent,
    cursor_zone: behavior.cursorZone,
    focused_product: mapFocusedProduct(),
    ...mapCartFields(),
  };
}

/** Emit `page_entered` for the current URL and start a new visit clock. */
export function reportPageEntered(reason: PageVisitEnterReason): void {
  if (typeof window === 'undefined') return;

  visitPathname = currentPathname();
  visitStartedAt = Date.now();
  leftForCurrentVisit = false;
  visitEverStarted = true;

  reportWidgetEvent('page_entered', {
    pathname: visitPathname,
    page_type: resolvePageType(),
    reason,
  });
}

/**
 * Emit `page_left` with a behavior snapshot for the visit that is ending.
 * Idempotent per visit (SPA navigate + pagehide must not double-count).
 * No-op until a visit has started (human-gate may delay first enter).
 */
export function reportPageLeft(reason: PageVisitLeaveReason): void {
  if (typeof window === 'undefined') return;
  if (!visitEverStarted) return;
  if (leftForCurrentVisit) return;

  leftForCurrentVisit = true;
  reportWidgetEvent('page_left', buildLeavePayload(reason));
}

function shouldWaitForHuman(): boolean {
  return isHumanGateEnabled() && !isHumanReady();
}

/**
 * Track site page visits for analytics: `page_entered` / `page_left` with
 * dwell + scroll / cursor / focus when behavior watchers are active.
 *
 * When human-gate is on, the first `page_entered` waits for engagement.
 * Install early in bootstrap (before React mounts behavior watchers) so that
 * on SPA navigate this listener runs first and snapshots before reset.
 */
export function installPageVisitAnalytics(): () => void {
  if (typeof window === 'undefined') return () => {};
  if (installed) return () => {};

  installed = true;
  visitEverStarted = false;
  leftForCurrentVisit = false;
  visitPathname = currentPathname();

  const startLoadVisit = () => {
    if (visitEverStarted) return;
    reportPageEntered('load');
  };

  if (shouldWaitForHuman()) {
    unsubscribeHuman = subscribeHumanReady(() => {
      unsubscribeHuman = null;
      startLoadVisit();
    });
  } else {
    startLoadVisit();
  }

  const onPageHide = () => {
    reportPageLeft('pagehide');
  };

  window.addEventListener('pagehide', onPageHide);

  const stopNavigation = watchPageNavigation({
    onNavigate: () => {
      if (!visitEverStarted) {
        // Still waiting for human — track the current URL for when we enter.
        visitPathname = currentPathname();
        return;
      }
      reportPageLeft('navigate');
      reportPageEntered('navigate');
    },
  });

  return () => {
    window.removeEventListener('pagehide', onPageHide);
    stopNavigation();
    unsubscribeHuman?.();
    unsubscribeHuman = null;
    installed = false;
    visitEverStarted = false;
  };
}
